import React, { useState, useEffect } from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

export interface PdfViewerFile {
  url: string;
  label?: string;
  kind?: 'invoice' | 'delivery_note' | 'statement' | 'receipt' | 'other';
}

interface PdfViewerProps {
  url?: string;
  files?: PdfViewerFile[];
  title?: string;
  height?: string;
}

const PDFJS_VERSION = '3.11.174';
const PDFJS_CDN = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}`;
const HUB = 'https://invoice-review.system-0f7.workers.dev';

function extractRowid(url: string): string | null {
  const m = url.match(/\/pdf\/(\d+)(?:\.0+)?(?:[?/]|$)/);
  return m ? m[1] : null;
}

// Probe PDF render flag via HEAD request to Worker.
// When true, PDF is known to not render correctly in pdf.js (subset font issues).
// Switch to server-side rasterized PNG images for accurate visual display.
// Returns { flagged: boolean, missing: boolean } — missing when worker says 502.
async function probeRenderFlag(url: string): Promise<{ flagged: boolean; missing: boolean; status: number }> {
  try {
    const r = await fetch(url, { method: 'HEAD' });
    return {
      flagged: r.headers.get('X-PDF-Render-Flag') === 'true',
      missing: r.status === 502 || r.status === 404,
      status: r.status,
    };
  } catch {
    return { flagged: false, missing: false, status: 0 };
  }
}

async function fetchPageCount(rowid: string): Promise<number | null> {
  try {
    const r = await fetch(`${HUB}/pdf/${rowid}/info`);
    if (!r.ok) return null;
    const j = await r.json();
    return j?.pages || null;
  } catch {
    return null;
  }
}

/**
 * PDF viewer with automatic fallback to server-side rasterization.
 *
 * Flow:
 *  1. HEAD request to PDF URL → check X-PDF-Render-Flag
 *  2. If flag=false: display via pdf.js (with CMap) as normal
 *  3. If flag=true: PDF fonts don't render in browser. Fetch page count then
 *     display PNG images rendered server-side via headless Chromium
 *     (Cloudflare Browser Rendering) — full CJK font support, identical to
 *     opening in Acrobat.
 */
export const PdfViewer: React.FC<PdfViewerProps> = ({ url, files, title, height = '600px' }) => {
  const resolvedFiles: PdfViewerFile[] = files && files.length > 0
    ? files
    : (url ? [{ url, label: title }] : []);
  const [activeIndex, setActiveIndex] = useState(0);
  const [renderMode, setRenderMode] = useState<'pdfjs' | 'image' | 'missing' | 'probing'>('probing');
  const [pageCount, setPageCount] = useState<number>(1);
  const [missingStatus, setMissingStatus] = useState<number>(0);
  const safeIndex = Math.min(activeIndex, Math.max(0, resolvedFiles.length - 1));
  const current = resolvedFiles[safeIndex];

  useEffect(() => {
    if (!current) return;
    let cancelled = false;
    setRenderMode('probing');
    (async () => {
      const probe = await probeRenderFlag(current.url);
      if (cancelled) return;
      // Worker returned 502 / 404 — true Missing PDF: try image fallback before giving up
      if (probe.missing) {
        const rid = extractRowid(current.url);
        if (rid) {
          // Try the image endpoint — it may still work via Browser Rendering even if /pdf is broken
          try {
            const imgResp = await fetch(`${HUB}/pdf/${rid}/image/1`, { method: 'HEAD' });
            if (!cancelled && imgResp.ok && imgResp.headers.get('content-type')?.startsWith('image/')) {
              const pc = await fetchPageCount(rid);
              if (cancelled) return;
              setPageCount(pc || 1);
              setRenderMode('image');
              return;
            }
          } catch {}
        }
        if (!cancelled) {
          setMissingStatus(probe.status);
          setRenderMode('missing');
        }
        return;
      }
      if (probe.flagged) {
        const rid = extractRowid(current.url);
        if (rid) {
          const pc = await fetchPageCount(rid);
          if (cancelled) return;
          setPageCount(pc || 1);
          setRenderMode('image');
          return;
        }
      }
      setRenderMode('pdfjs');
    })();
    return () => { cancelled = true; };
  }, [current?.url]);

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [],
    toolbarPlugin: {
      fullScreenPlugin: {},
      searchPlugin: {},
    },
  });

  if (!current) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8a8580', fontSize: '0.85rem' }}>
        証憑なし
      </div>
    );
  }

  const rid = extractRowid(current.url);
  const showImage = renderMode === 'image' && rid;

  return (
    <div style={{ height, width: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {resolvedFiles.length > 1 && (
        <div
          style={{
            display: 'flex',
            gap: '4px',
            padding: '6px 8px',
            borderBottom: '1px solid #e5e4e2',
            background: '#fafafa',
            overflowX: 'auto',
            flexShrink: 0,
          }}
        >
          {resolvedFiles.map((f, i) => (
            <button
              key={f.url + '_' + i}
              onClick={() => setActiveIndex(i)}
              title={f.url}
              style={{
                padding: '4px 10px',
                fontSize: '0.7rem',
                borderRadius: '10px',
                border: '1px solid ' + (i === safeIndex ? '#2e7cf6' : '#d5d3d0'),
                background: i === safeIndex ? '#ebf3ff' : '#fff',
                color: i === safeIndex ? '#2e7cf6' : '#6b6560',
                cursor: 'pointer',
                fontWeight: i === safeIndex ? 600 : 400,
                whiteSpace: 'nowrap',
              }}
            >
              {f.label || `証憑 ${i + 1}`}
            </button>
          ))}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, overflow: showImage ? 'auto' : 'hidden', background: '#f5f5f5' }}>
        {renderMode === 'probing' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8a8580', fontSize: '0.8rem' }}>
            読み込み中...
          </div>
        )}
        {renderMode === 'missing' && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: '#9a4a2c', fontSize: '0.85rem', padding: '20px', gap: '10px'
          }}>
            <div style={{ fontWeight: 600 }}>PDF 取得失敗 (HTTP {missingStatus})</div>
            <div style={{ fontSize: '0.75rem', textAlign: 'center', maxWidth: '520px', lineHeight: 1.5 }}>
              ARICA scraper の取得が未完了か、 添付ファイルが PDF ではない可能性があります。
              <br />しばらくしてからリロード、 または ARICA 側で再 scrape を依頼してください。
            </div>
            <a href={current.url} target="_blank" rel="noreferrer" style={{ color: '#2e7cf6', fontSize: '0.75rem' }}>
              直接 URL を開く (debug 用)
            </a>
          </div>
        )}
        {renderMode === 'pdfjs' && (
          <Worker workerUrl={`${PDFJS_CDN}/build/pdf.worker.min.js`}>
            <Viewer
              key={current.url}
              fileUrl={current.url}
              plugins={[defaultLayoutPluginInstance]}
              defaultScale={1}
              theme={{ theme: 'light' }}
              characterMap={{
                url: `${PDFJS_CDN}/cmaps/`,
                isCompressed: true,
              }}
            />
          </Worker>
        )}
        {showImage && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '8px' }}>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map(pageNum => (
              <img
                key={`${current.url}-p${pageNum}`}
                src={`${HUB}/pdf/${rid}/image/${pageNum}`}
                alt={`page ${pageNum}`}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                  background: '#fff',
                }}
                loading={pageNum === 1 ? 'eager' : 'lazy'}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Modal version - full screen PDF viewer overlay
 */
export const PdfViewerModal: React.FC<{
  url: string;
  title: string;
  isOpen: boolean;
  onClose: () => void;
}> = ({ url, title, isOpen, onClose }) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const [renderMode, setRenderMode] = useState<'pdfjs' | 'image' | 'missing' | 'probing'>('probing');
  const [pageCount, setPageCount] = useState<number>(1);
  const [missingStatus, setMissingStatus] = useState<number>(0);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setRenderMode('probing');
    (async () => {
      const probe = await probeRenderFlag(url);
      if (cancelled) return;
      if (probe.missing) {
        const rid = extractRowid(url);
        if (rid) {
          try {
            const imgResp = await fetch(`${HUB}/pdf/${rid}/image/1`, { method: 'HEAD' });
            if (!cancelled && imgResp.ok && imgResp.headers.get('content-type')?.startsWith('image/')) {
              const pc = await fetchPageCount(rid);
              if (cancelled) return;
              setPageCount(pc || 1);
              setRenderMode('image');
              return;
            }
          } catch {}
        }
        if (!cancelled) {
          setMissingStatus(probe.status);
          setRenderMode('missing');
        }
        return;
      }
      if (probe.flagged) {
        const rid = extractRowid(url);
        if (rid) {
          const pc = await fetchPageCount(rid);
          if (cancelled) return;
          setPageCount(pc || 1);
          setRenderMode('image');
          return;
        }
      }
      setRenderMode('pdfjs');
    })();
    return () => { cancelled = true; };
  }, [url, isOpen]);

  if (!isOpen) return null;

  const rid = extractRowid(url);
  const showImage = renderMode === 'image' && rid;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 2000,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 20px',
        background: '#252525',
        color: '#fff',
        flexShrink: 0,
      }}>
        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{title}</span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: '1px solid rgba(255,255,255,0.3)',
            color: '#fff',
            padding: '6px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.8125rem',
          }}
        >
          閉じる
        </button>
      </div>
      <div style={{ flex: 1, overflow: showImage ? 'auto' : 'hidden', background: '#fff' }}>
        {renderMode === 'missing' && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: '#9a4a2c', fontSize: '0.95rem', padding: '40px', gap: '14px'
          }}>
            <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>PDF 取得失敗 (HTTP {missingStatus})</div>
            <div style={{ fontSize: '0.8125rem', textAlign: 'center', maxWidth: '560px', lineHeight: 1.6 }}>
              ARICA scraper の取得が未完了か、 添付ファイルが PDF ではない可能性があります。
              <br />しばらくしてからリロード、 または ARICA 側で再 scrape を依頼してください。
            </div>
            <a href={url} target="_blank" rel="noreferrer" style={{ color: '#2e7cf6', fontSize: '0.8125rem' }}>
              直接 URL を開く (debug 用)
            </a>
          </div>
        )}
        {renderMode === 'pdfjs' && (
          <Worker workerUrl={`${PDFJS_CDN}/build/pdf.worker.min.js`}>
            <Viewer
              fileUrl={url}
              plugins={[defaultLayoutPluginInstance]}
              defaultScale={1.2}
              characterMap={{
                url: `${PDFJS_CDN}/cmaps/`,
                isCompressed: true,
              }}
            />
          </Worker>
        )}
        {showImage && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '16px' }}>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map(pageNum => (
              <img
                key={`${url}-p${pageNum}`}
                src={`${HUB}/pdf/${rid}/image/${pageNum}`}
                alt={`page ${pageNum}`}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                }}
                loading={pageNum === 1 ? 'eager' : 'lazy'}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
